import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../../services/api';
import './Login.css';

// Site Dudes Logo
const siteDudesLogo = '/sd_logo_480px_136px.png';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string>('6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI');
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Fetch reCAPTCHA site key
  React.useEffect(() => {
    api.get<{ site_key: string }>('/public/recaptcha-site-key')
      .then(response => {
        if (response.data?.site_key) {
          setRecaptchaSiteKey(response.data.site_key);
        }
      })
      .catch(error => {
        console.error('Failed to fetch reCAPTCHA site key:', error);
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      return;
    }
    
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetLoading(true);
    setResetSuccess(false);

    try {
      await api.post('/auth/reset-password', { email: resetEmail });
      setResetSuccess(true);
      setResetEmail('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset password email.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo-section">
          <img src={siteDudesLogo} alt="Site Dudes Logo" className="login-logo" />
        </div>
        <h2>Login</h2>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          <div className="form-group">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              onChange={handleRecaptchaChange}
              onExpired={handleRecaptchaExpired}
            />
          </div>
          <button type="submit" disabled={loading || !recaptchaToken} className="login-button">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        <div className="login-info">
          <p>Admin or Company users can login here</p>
          <div style={{ marginTop: '15px' }}>
            <button
              type="button"
              onClick={() => {
                setShowResetPassword(!showResetPassword);
                setError('');
                setResetSuccess(false);
              }}
              className="reset-password-link"
            >
              {showResetPassword ? '← Back to Login' : 'Forgot Password?'}
            </button>
          </div>
        </div>
        
        {showResetPassword && (
          <div className="reset-password-form">
            <h3>Reset Password</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleResetPassword}>
              {resetSuccess && (
                <div className="success-message">
                  Password reset email sent! Please check your inbox.
                </div>
              )}
              {error && <div className="error-message">{error}</div>}
              <div className="form-group">
                <label htmlFor="reset-email">Email Address</label>
                <input
                  type="email"
                  id="reset-email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </div>
              <button type="submit" disabled={resetLoading} className="login-button">
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;

